import { byGridOrder, dayOf } from './talentReviewWriteOrder';

export const RETENTION_RISKS = ['LOW', 'MEDIUM', 'HIGH'] as const;

export type RetentionRiskLevel = (typeof RETENTION_RISKS)[number];

export const retentionRiskLabelKey = (risk: RetentionRiskLevel) =>
	`talentReviews.v2.sidebar.card.retentionRisk.${risk}`;

/*
 * Hover copy for a level. Absent in Locize means the chip has no tooltip yet.
 */
export const retentionRiskDescriptionKey = (risk: RetentionRiskLevel) =>
	`talentReviews.v2.sidebar.card.retentionRiskDescription.${risk}`;

export type RetentionWrite = {
	userID: string;
	groupID: string;
	/** Absent on older writes; their day is the date of `createdAt`. */
	localDate?: string;
	removed?: number;
	retentionRisk?: string | null;
	createdAt: Date;
};

export type RetentionRiskUser = {
	firstName: string;
	lastName: string;
	avatarUrl?: string | null;
};

export type RetentionRiskPerson = {
	userID: string;
	firstName: string;
	lastName: string;
	avatarUrl?: string | null;
	department: string;
	retentionRisk: RetentionRiskLevel;
	updatedAt: Date;
};

const isRetentionRisk = (value: string | null | undefined): value is RetentionRiskLevel =>
	value === 'LOW' || value === 'MEDIUM' || value === 'HIGH';

/*
 * A rating or an explicit clear (`''`). Every write stores one now; absent
 * only occurs on older writes and means that write did not rate.
 */
const storesRisk = (write: RetentionWrite) =>
	write.retentionRisk === '' || isRetentionRisk(write.retentionRisk);

/*
 * When the current rating was chosen: walking back from `source` in grid
 * order, the oldest write of the run that stores the same rating. Writes that
 * left the field off do not end the run or move the date.
 */
const riskIntroducedAt = (
	inGridOrder: RetentionWrite[],
	source: RetentionWrite,
	risk: RetentionRiskLevel,
): Date => {
	let introduced = source.createdAt;

	for (const write of inGridOrder.slice(inGridOrder.indexOf(source) + 1)) {
		if (!storesRisk(write)) {
			continue;
		}

		if (write.retentionRisk !== risk) {
			break;
		}

		introduced = write.createdAt;
	}

	return introduced;
};

/**
 * One row per person, matching the risk the grid shows on `localDate`.
 * The placement is the first write in grid order (see `byGridOrder`). Someone
 * whose placement removed them is off the grid and has no risk. The rating or
 * `''` on that write is theirs, as on the grid. Only an older placement that
 * left the field off falls back on the next write in grid order that stored
 * one. The date is when that rating was chosen, not when a later edit copied
 * it.
 * @param writes placement writes for the selected groups
 * @param localDate the viewed day, YYYY-MM-DD
 * @param eligibleIDs people that count for the current filters
 * @param users names and avatar URLs, keyed by userID
 * @param groups department names, keyed by groupID
 */
export function retentionRiskPeople(
	writes: RetentionWrite[],
	localDate: string,
	eligibleIDs: Set<string>,
	users: ReadonlyMap<string, RetentionRiskUser>,
	groups: ReadonlyMap<string, string>,
): RetentionRiskPerson[] {
	const byUser = new Map<string, RetentionWrite[]>();

	for (const write of [...writes].sort(byGridOrder)) {
		if (dayOf(write) > localDate || !eligibleIDs.has(write.userID)) {
			continue;
		}

		const userWrites = byUser.get(write.userID);

		if (userWrites) {
			userWrites.push(write);
		} else {
			byUser.set(write.userID, [write]);
		}
	}

	const people: RetentionRiskPerson[] = [];

	for (const [userID, userWrites] of byUser) {
		const user = users.get(userID);
		const [placement] = userWrites;

		if (!user || !placement || placement.removed) {
			continue;
		}

		const source = userWrites.find(storesRisk);
		const risk = source?.retentionRisk;

		if (!source || !isRetentionRisk(risk)) {
			continue;
		}

		people.push({
			userID,
			firstName: user.firstName,
			lastName: user.lastName,
			avatarUrl: user.avatarUrl,
			department: groups.get(placement.groupID) ?? '',
			retentionRisk: risk,
			updatedAt: riskIntroducedAt(userWrites, source, risk),
		});
	}

	return people.sort((left, right) =>
		`${left.lastName} ${left.firstName}`.localeCompare(`${right.lastName} ${right.firstName}`),
	);
}
